<template>
  <div>
    <LayoutPageHeader 
      :title="`Author: ${author?.name || 'Loading...'}`" 
      :background-image="'https://cdn-media.geekcafe.com/a0d4d17b-ec8f-4f28-9169-97e3e5bb4d37/person-looking-at-phone-and-at-macbook-pro-1181244_1920x400.jpg'"
    />

    <main class="container my-5">
      <div v-if="author">
        <div class="author-bio mb-5 p-4 bg-light rounded">
          <div class="row align-items-center">
            <div class="col-md-3 text-center">
              <img :src="authorImage" :alt="author.name" class="rounded-circle img-fluid mb-3" style="max-width: 150px;">
            </div>
            <div class="col-md-9">
              <h2>{{ author.name }}</h2>
              <div v-if="author.bio" v-html="author.bio"></div>
            </div>
          </div>
        </div>
        
        <h3 class="mb-4">Posts by {{ author.name }}</h3>
        
        <div class="row">
          <div v-for="post in authorPosts" :key="post._path" class="col-md-6 mb-4">
            <BlogCard :post="post" />
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-12 text-center">
            <NuxtLink to="/blog" class="btn btn-outline-primary">
              <i class="fas fa-arrow-left me-2"></i> Back to All Blogs
            </NuxtLink>
          </div>
        </div>
      </div>
      <div v-else>
        <p>Author not found.</p>
      </div>
    </main>
  </div>
</template>

<script setup>
import { useRoute } from 'vue-router'
import { computed } from 'vue'
import SparkMD5 from 'spark-md5'

const route = useRoute()
const slug = route.params.slug

// Fetch author details
const { data: authorData } = await useAsyncData(`author-${slug}`, async () => {
  try {
    const response = await $fetch(`/api/content/authors/${slug}`)
    if (!response || !response.content) return null

    const lines = response.content.split('\n')
    const fm = {}
    if (lines[0] === '---') {
      for (let i = 1; i < lines.length; i++) {
        if (lines[i] === '---') break
        const separatorIndex = lines[i].indexOf(':')
        if (separatorIndex !== -1) {
          const key = lines[i].substring(0, separatorIndex).trim()
          const value = lines[i].substring(separatorIndex + 1).trim()
          fm[key] = value
        }
      }
    }
    return fm
  } catch {
    return null
  }
})

const author = computed(() => authorData.value)

const authorImage = computed(() => {
  if (author.value?.image) {
    return author.value.image;
  }
  if (author.value?.gravatar) {
    const hash = SparkMD5.hash(author.value.gravatar.trim().toLowerCase());
    return `https://www.gravatar.com/avatar/${hash}?d=identicon`;
  }
  return '/img/placeholders/silhouette.svg';
});

// Fetch all posts and filter by author
const { data: authorPosts } = await useAsyncData(`posts-by-author-${slug}`, async () => {
  if (!slug) return []
  try {
    const allPosts = await $fetch('/api/posts')
    return allPosts.filter(post => {
      const isPrimaryAuthor = post.author && post.author.slug === slug;
      const isCoAuthor = post.authors && Array.isArray(post.authors) && post.authors.includes(slug);
      return isPrimaryAuthor || isCoAuthor;
    })
  } catch {
    return []
  }
})

// Set page metadata
useHead({
  title: `Author: ${author.value?.name || 'Author'} | Geek Cafe`,
  meta: [
    { name: 'description', content: `Posts by ${author.value?.name || 'Author'}` }
  ]
})
</script>

<style scoped>
.author-bio {
  padding: 2rem;
  background-color: #f8f9fa;
  border-radius: 0.5rem;
}
</style>
